import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RecipeDetail, RecipeNoteDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { recipeKeys } from '../../pages/recipes/queries';
import { moveItem } from '../../pages/recipes/recipe-form';

const NOTE_MAX = 1000;

const iconBtn =
  'flex size-11 items-center justify-center rounded-lg border border-line text-ink disabled:opacity-40';
const textBtn =
  'min-h-11 rounded-lg border border-line px-3 text-sm font-semibold disabled:opacity-50';

/**
 * The household's numbered notes on a recipe (D26): ordered 1, 2, 3 …, with
 * inline add, edit, delete and up/down reorder. Only this household ever sees
 * them, including on a public recipe (D12). Every write returns the full list,
 * which replaces the cached recipe's `notes`.
 */
export function RecipeNotes({ recipeId, notes }: { recipeId: string; notes: RecipeNoteDto[] }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const setNotes = (next: RecipeNoteDto[]) =>
    qc.setQueryData<RecipeDetail>(recipeKeys.detail(recipeId), (old) =>
      old ? { ...old, notes: next } : old,
    );
  const onSaved = ({ notes: next }: { notes: RecipeNoteDto[] }) => setNotes(next);

  const add = useMutation({
    mutationFn: (text: string) => api.recipes.addNote(recipeId, { text }),
    onSuccess: (res) => {
      onSaved(res);
      setDraft('');
    },
  });
  const edit = useMutation({
    mutationFn: ({ noteId, text }: { noteId: string; text: string }) =>
      api.recipes.updateNote(recipeId, noteId, { text }),
    onSuccess: (res) => {
      onSaved(res);
      setEditingId(null);
    },
  });
  const remove = useMutation({
    mutationFn: (noteId: string) => api.recipes.removeNote(recipeId, noteId),
    onSuccess: (res) => {
      onSaved(res);
      setConfirmId(null);
    },
  });
  const reorder = useMutation({
    mutationFn: (ordered: RecipeNoteDto[]) =>
      api.recipes.reorderNotes(
        recipeId,
        ordered.map((n) => n.id),
      ),
    // Renumber locally straight away; the server's answer (or a refetch on error) wins.
    onMutate: (ordered) => setNotes(ordered.map((n, i) => ({ ...n, number: i + 1 }))),
    onSuccess: onSaved,
    onError: () => qc.invalidateQueries({ queryKey: recipeKeys.detail(recipeId) }),
  });

  const busy = add.isPending || edit.isPending || remove.isPending || reorder.isPending;
  const error = add.error ?? edit.error ?? remove.error ?? reorder.error;

  function onAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = draft.trim();
    if (text) add.mutate(text);
  }

  function startEdit(note: RecipeNoteDto) {
    setConfirmId(null);
    setEditingId(note.id);
    setEditText(note.text);
  }

  return (
    <div className="flex flex-col gap-3">
      {notes.length === 0 ? (
        <p className="text-sm text-muted">
          No notes yet. Jot down tweaks like “use half the salt” — only your household sees them.
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-line" aria-label="Notes">
          {notes.map((note, i) => {
            const label = `note ${note.number}`;
            return (
              <li key={note.id} className="flex flex-col gap-2 py-2">
                <div className="flex gap-3">
                  <span
                    aria-hidden
                    className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-orange-soft text-sm font-semibold text-orange-ink"
                  >
                    {note.number}
                  </span>
                  {editingId === note.id ? (
                    <form
                      className="flex min-w-0 flex-1 flex-col gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const text = editText.trim();
                        if (text) edit.mutate({ noteId: note.id, text });
                      }}
                    >
                      <label htmlFor={`note-edit-${note.id}`} className="sr-only">
                        Edit {label}
                      </label>
                      <textarea
                        id={`note-edit-${note.id}`}
                        rows={2}
                        maxLength={NOTE_MAX}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        autoFocus
                        className="rounded-lg border border-line bg-surface p-3 text-base text-ink"
                      />
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={busy || editText.trim() === ''}
                          className={`${textBtn} border-brand bg-brand text-white`}
                        >
                          {edit.isPending ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className={`${textBtn} text-ink`}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="min-w-0 flex-1">
                      <span className="sr-only">Note {note.number}: </span>
                      <p className="text-base whitespace-pre-line text-ink">{note.text}</p>
                      {note.authorName ? (
                        <p className="mt-0.5 text-xs text-muted">{note.authorName}</p>
                      ) : null}
                    </div>
                  )}
                </div>
                {editingId === note.id ? null : confirmId === note.id ? (
                  <div className="flex flex-wrap items-center gap-2 pl-10" role="group">
                    <span className="text-sm text-ink">Delete {label}?</span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => remove.mutate(note.id)}
                      className={`${textBtn} text-fail`}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(null)}
                      className={`${textBtn} text-ink`}
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-1 pl-10">
                    <button
                      type="button"
                      className={iconBtn}
                      disabled={busy || i === 0}
                      aria-label={`Move ${label} up`}
                      onClick={() => reorder.mutate(moveItem(notes, i, i - 1))}
                    >
                      <span aria-hidden>↑</span>
                    </button>
                    <button
                      type="button"
                      className={iconBtn}
                      disabled={busy || i === notes.length - 1}
                      aria-label={`Move ${label} down`}
                      onClick={() => reorder.mutate(moveItem(notes, i, i + 1))}
                    >
                      <span aria-hidden>↓</span>
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => startEdit(note)}
                      aria-label={`Edit ${label}`}
                      className={`${textBtn} text-brand`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setEditingId(null);
                        setConfirmId(note.id);
                      }}
                      aria-label={`Delete ${label}`}
                      className={`${textBtn} text-fail`}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {error ? <Alert tone="error">{messageForError(error)}</Alert> : null}

      <form onSubmit={onAdd} className="flex flex-col gap-2">
        <label htmlFor="new-note" className="text-sm font-medium text-ink">
          Add a note
        </label>
        <textarea
          id="new-note"
          rows={2}
          maxLength={NOTE_MAX}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Double the sauce; kids prefer it mild…"
          className="rounded-lg border border-line bg-surface p-3 text-base text-ink placeholder:text-muted"
        />
        <button
          type="submit"
          disabled={busy || draft.trim() === ''}
          className="min-h-11 self-start rounded-lg bg-brand px-4 text-sm font-semibold text-white disabled:opacity-50"
        >
          {add.isPending ? 'Adding…' : `Add note ${notes.length + 1}`}
        </button>
      </form>
    </div>
  );
}
